import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalBody,
  FormControl,
  FormLabel,
  HStack,
  FormErrorMessage,
  ModalCloseButton,
  Grid,
  GridItem,
  useMediaQuery,
} from '@chakra-ui/react'

import { useTranslation } from 'react-i18next'
import ReactSelect from 'components/form/react-select'
import { SelectOption } from 'types/transaction.type'
import { Button } from 'components/button/button'
import { Controller, useForm } from 'react-hook-form'
import FileDragDrop from 'components/file-drag-drop/file-drag-drop'
import { readFileContent } from 'api/vendor-details'

export const UploadFilesModal: React.FC<any> = ({ isOpen, onClose, handleUpload, uploadedFiles, documentTypes }) => {
  const { t } = useTranslation()
  const [isMobile] = useMediaQuery('(max-width: 480px)')
  const [modalSize, setModalSize] = useState<string>('3xl')

  useEffect(() => {
    isMobile ? setModalSize('sm') : setModalSize('3xl')
  }, [isMobile])

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      size={modalSize}
      variant="custom"
    >
      <ModalOverlay />
      <ModalContent minH="317px">
        <ModalHeader>{t('uploadDocument')}</ModalHeader>
        <ModalCloseButton _focus={{ outline: 'none' }} _hover={{ bg: 'blue.50' }} />
        <ModalBody>
          <MultipleFilesUploadForm
            documentTypes={documentTypes}
            isTypeEnabled={false}
            handleUpload={files => handleUpload(files)}
            uploadedFiles={uploadedFiles}
          />
        </ModalBody>
        <ModalFooter>
          <HStack spacing="16px">
            <Button
              variant="outline"
              onClick={() => {
                onClose()
              }}
              colorScheme="brand"
            >
              {t('cancel')}
            </Button>
            <Button type="submit" colorScheme="brand" form="uploadLineItemsFiles">
              {t('save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export type MultipleFileUploadProps = {
  documentTypes: SelectOption[]
  isTypeEnabled?: boolean
  handleUpload: (documents) => void
  handleDelete?: (documents) => void
  uploadedFiles?: any[]
}

type FormValues = {
  documentType: SelectOption | null
  chooseFile: any[]
}

const MultipleFilesUploadForm = (props: MultipleFileUploadProps) => {
  const { t } = useTranslation()
  const { documentTypes, isTypeEnabled, handleUpload, uploadedFiles } = props
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({
    defaultValues: { documentType: documentTypes?.length === 1 ? documentTypes[0] : null, chooseFile: uploadedFiles },
  })
  const values = getValues()

  const creatDocumentsPayload = async (documents: Array<any>, documentType: string) => {
    if (documents) {
      const results = await Promise.all(
        documents?.map(async (document: any, index: number) => {
          let doc = {}
          if (!document?.fileObject && !document?.s3Url) {
            const fileContents = await readFileContent(document)
            doc = {
              path: document.name,
              fileType: document.name,
              fileObject: fileContents,
              fileObjectContentType: document.type,
              size: document.size,
              documentType,
              name: document.name,
            }
          } else {
            doc = document
          }
          return doc
        }),
      )
      return results
    }
  }

  const onSubmit = async formValues => {
    const documentPayload = await creatDocumentsPayload(
      formValues.chooseFile,
      formValues?.documentTypes?.value?.toString(),
    )
    handleUpload(documentPayload)
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, error => {
        console.log(error)
      })}
      id="uploadLineItemsFiles"
    >
      <HStack h="90px">
        <Grid gridTemplateColumns={{ md: '215px 215px 215px', sm: '' }} gap="15px">
          <GridItem>
            <FormControl data-testid="document-type">
              <FormLabel htmlFor="documentType" variant="strong-label" size="md">
                {t('documentType')}
              </FormLabel>
              <Controller
                control={control}
                name="documentType"
                rules={{ required: isTypeEnabled ? 'Document type is required' : false }}
                render={() => {
                  return (
                    <>
                      <ReactSelect
                        isDisabled={!isTypeEnabled}
                        options={[{ label: 'image', value: 'image' }]}
                        selectProps={{ isBorderLeft: isTypeEnabled, menuHeight: '200px' }}
                        value={documentTypes}
                      />
                    </>
                  )
                }}
              />
            </FormControl>
          </GridItem>
        </Grid>
      </HStack>

      <>
        <FormControl isInvalid={!!errors?.chooseFile}>
          <Controller
            control={control}
            name="chooseFile"
            rules={{
              validate: files => {
                const fileLengthExceeded = files?.some(file => file?.name?.length > 255)
                return fileLengthExceeded ? 'File name length should be less than 255' : true
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <>
                  <FileDragDrop
                    isRequired
                    testId="choose-document"
                    name={field.name}
                    onUpload={(docs: any) => {
                      field.onChange(docs)
                    }}
                    documentType={values?.documentType?.value?.toString()}
                    uploadedFiles={uploadedFiles}
                  />
                  <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
                </>
              )
            }}
          />
        </FormControl>
      </>
    </form>
  )
}
